// Ch76 · the Definition-of-Done instrument on a screen.
//
// Grant's ratified bar (2026-09-01): "all eight rows green simultaneously for
// 30 consecutive days, each read by an instrument, nobody logging in." This
// band renders GET /admin/definition-of-done — eight lane tiles plus the
// streak — under the AdminTruthBand rules:
//
//   status "green"    -> the word green.
//   status "red"      -> the word red.
//   status "unknown"  -> the word unknown, AMBER, with the backend's reason.
//                        A lane whose reader failed or whose instrument lives
//                        outside LN Pro is not a verdict either way.
//
// A streak the backend could not read (series table unreadable) renders as
// "unknown", never as 0. Self-fetching, like AdminTruthBand: it must not join
// the dashboard's Promise.all.

import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  fetchDefinitionOfDone,
  type DefinitionOfDoneResponse,
  type DodRow,
  type DodStatus,
} from "../../services/definitionOfDoneService";

const STATUS_COLOR: Record<DodStatus, string> = {
  green: "success.main",
  red: "error.main",
  unknown: "warning.main",
};

export function laneTestId(lane: string): string {
  return `dod-row-${lane.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function LaneTile({ row }: { row: DodRow }) {
  const status: DodStatus =
    row.status === "green" || row.status === "red" ? row.status : "unknown";
  return (
    <Box data-testid={laneTestId(row.row)}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {row.row}
      </Typography>
      <Tooltip title={`${row.condition} — ${row.definition_id}`} placement="top">
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: STATUS_COLOR[status], lineHeight: 1.3 }}
          data-testid={`${laneTestId(row.row)}-status`}
        >
          {status}
        </Typography>
      </Tooltip>
      {status !== "green" && row.note && (
        <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
          {row.note}
        </Typography>
      )}
    </Box>
  );
}

export function streakHeadline(overall: DefinitionOfDoneResponse["overall"]): string {
  if (overall.streak_status !== "ok" || overall.consecutive_green_days == null) {
    return `streak unknown${overall.note ? ` — ${overall.note}` : ""}`;
  }
  const n = overall.consecutive_green_days;
  const base = `${n} of ${overall.required_consecutive_days} consecutive green days`;
  return overall.streak_start ? `${base} since ${overall.streak_start}` : base;
}

const DefinitionOfDoneBand: React.FC = () => {
  const [report, setReport] = React.useState<DefinitionOfDoneResponse | null>(null);
  const [state, setState] = React.useState<"loading" | "ok" | "error">("loading");

  const load = React.useCallback(async () => {
    setState("loading");
    try {
      setReport(await fetchDefinitionOfDone());
      setState("ok");
    } catch {
      setReport(null);
      setState("error");
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const overall = report?.overall;
  const streakUnknown = !overall || overall.streak_status !== "ok" || overall.consecutive_green_days == null;

  return (
    <Card sx={{ mb: 3 }} data-testid="dod-band">
      <CardContent>
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            Definition of done — 8 lanes, 30-day bar
          </Typography>
          {state === "ok" && report && (
            <Typography variant="caption" color="text.disabled">
              read {new Date(report.generated_at).toLocaleString()}
            </Typography>
          )}
        </Box>

        {state === "loading" && (
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton width={90} height={18} />
                <Skeleton width={70} height={28} />
              </Grid>
            ))}
          </Grid>
        )}

        {state === "error" && (
          <Box mt={1} display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="warning.main" data-testid="dod-error">
              Couldn't read the definition-of-done rows. No lane is being guessed in their place.
            </Typography>
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          </Box>
        )}

        {state === "ok" && report && overall && (
          <>
            <Box mt={0.5} display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: overall.all_green_today ? "success.main" : "text.primary" }}
                data-testid="dod-all-green-today"
              >
                all green today: {overall.all_green_today ? "yes" : "no"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: streakUnknown ? "warning.main" : "text.secondary" }}
                data-testid="dod-streak"
              >
                {streakHeadline(overall)}
              </Typography>
              <Typography variant="caption" color="text.disabled" data-testid="dod-measured">
                measured: {overall.measured_rows.length} of {report.rows.length} · unknown:{" "}
                {overall.unknown_rows.length}
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              {report.rows.map((row) => (
                <Grid item xs={6} md={3} key={row.row}>
                  <LaneTile row={row} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DefinitionOfDoneBand;
